import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { lineReveal, fadeUp } from "@/lib/motion";

const HERO_IMG =
  "https://images.unsplash.com/photo-1706958581603-dffa91fec580?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwyfHxtZWRpY2FsJTIwc3R1ZGVudCUyMHN0dWR5aW5nfGVufDB8fHx8MTc4NDc5Mzk1M3ww&ixlib=rb-4.1.0&q=85";

const LINES = ["Kazakistan'da", "Tıp ve Diş Hekimliğine", "YKS Puanı Olmadan"];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section id="hero" ref={ref} data-testid="hero-section" className="relative pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden">
      {/* soft ambient shapes */}
      <div className="pointer-events-none absolute -top-24 -left-24 w-96 h-96 rounded-full bg-[#1B19A8]/5 blur-3xl" />
      <div className="pointer-events-none absolute top-40 right-0 w-96 h-96 rounded-full bg-[#FFE500]/10 blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-[1.15fr_0.85fr] gap-12 lg:gap-16 items-center">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-[#1B19A8]/10 bg-[#FFE500] px-4 py-1.5 mb-7 shadow-sm"
          >
            <Sparkles size={15} className="text-[#1B19A8]" />
            <span className="text-xs font-semibold uppercase tracking-[0.15em] text-[#1B19A8]">
              Sınavsız Kayıt · İngilizce Eğitim
            </span>
          </motion.div>

          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.98] text-[#1a1a1a]">
            {LINES.map((line, i) => (
              <span key={i} className="line-mask">
                <motion.span
                  className="block"
                  variants={lineReveal}
                  initial="hidden"
                  animate="show"
                  custom={i}
                >
                  {i === 2 ? (
                    <span className="text-[#1B19A8]">{line}</span>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
            <span className="line-mask">
              <motion.span className="block" variants={lineReveal} initial="hidden" animate="show" custom={3}>
                Başla.
              </motion.span>
            </span>
          </h1>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={5}
            className="mt-7 text-lg md:text-xl text-[#1a1a1a]/70 leading-relaxed max-w-xl"
          >
            Türk öğrenciler için sınavsız kayıt: İngilizce eğitimle Tıp, Diş Hekimliği,
            Eczacılık ve daha fazlasına doğrudan başvur.
          </motion.p>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={6}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <a
              href="#basvuru"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#1B19A8] px-7 py-4 text-base font-semibold text-white shadow-[0_12px_32px_rgba(31,78,95,0.3)] hover:bg-[#141178] active:scale-[0.98] transition-[background-color,transform]"
            >
              Ücretsiz Danışmanlık Al
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#bolumler"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center rounded-full border border-[#1a1a1a]/15 bg-white px-7 py-4 text-base font-semibold text-[#1a1a1a] hover:border-[#1B19A8] hover:text-[#1B19A8] active:scale-[0.98] transition-[color,border-color,transform]"
            >
              Bölümleri İncele
            </a>
          </motion.div>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={7}
            className="mt-10 flex items-center gap-8"
          >
            {[
              ["17+", "Anlaşmalı Üniversite"],
              ["%100", "İngilizce Programlar"],
              ["0", "YKS Puanı Şartı"],
            ].map(([n, l]) => (
              <div key={l}>
                <div className="font-display text-3xl font-bold text-[#1B19A8]">{n}</div>
                <div className="text-xs text-[#1a1a1a]/60 mt-1 max-w-[90px] leading-tight">{l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* image side */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="relative"
        >
          <div className="relative rounded-[2rem] overflow-hidden shadow-[0_30px_80px_rgba(31,78,95,0.25)] aspect-[4/5]">
            <motion.img
              src={HERO_IMG}
              alt="Kazakistan'da tıp eğitimi alan öğrenci"
              style={{ y, scale }}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a]/50 to-transparent" />
          </div>

          {/* floating badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.6 }}
            className="absolute -bottom-5 -left-5 sm:-left-8 bg-white rounded-2xl p-5 shadow-[0_20px_50px_rgba(0,0,0,0.12)] max-w-[220px]"
          >
            <div className="inline-block text-xs uppercase tracking-[0.15em] text-[#1B19A8] bg-[#FFE500] px-2 py-0.5 rounded-md font-bold mb-2">
              Sınavsız
            </div>
            <div className="font-display text-lg font-bold text-[#1a1a1a] leading-snug">
              Tıp & Diş Hekimliğine doğrudan kayıt
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
